import { BoxGeometry, Clock, BufferGeometry, Mesh, MeshLambertMaterial, Texture, TextureLoader, Vector3, WebGLRenderer, } from "three";
import { Cache, StyleUtils } from "../utils/internal";
import { DisparityMap } from "../scenegraph/internal";
import { isDefined, isNull, monitor } from "../internal";
/**
 * @enum GeomType
 */
export var GeomType;
(function (GeomType) {
    GeomType[GeomType["POINT"] = 0] = "POINT";
    GeomType[GeomType["LINE"] = 1] = "LINE";
    GeomType[GeomType["POLYGON"] = 2] = "POLYGON";
})(GeomType || (GeomType = {}));
export var CACHEITEMSTATE;
(function (CACHEITEMSTATE) {
    CACHEITEMSTATE[CACHEITEMSTATE["NOTREQUESTEDFOR"] = 0] = "NOTREQUESTEDFOR";
    CACHEITEMSTATE[CACHEITEMSTATE["LOADED"] = 1] = "LOADED";
    CACHEITEMSTATE[CACHEITEMSTATE["LOADING"] = 2] = "LOADING";
})(CACHEITEMSTATE || (CACHEITEMSTATE = {}));
/**
 * EngineThree Class
 * @class EngineThree
 * @param {string} canvasElement
 * @extends Engine
 * @constructor
 * @param {string} canvasElement
 */
export class EngineThree {
    // FIXME: This needs to be refactored and typed. This is a mess.
    constructor(options) {
        this.clock = new Clock();
        this.centroid = [0.0, 0.0, 0.0];
        /**
         * the distance between camera and ground floor, used for 2d-features
         * @type {number}
         */
        this.camtogrounddist = 2.5;
        /**
         * the actual inclination in deg
         * @type {number}
         */
        this.inclination = 0;
        /**
         * Defines the crossorigin attribute used in image requests
         * @type {string}
         */
        this.crossorigin = "anonymous";
        this.isLoading = [];
        this._onWebGLContextLost = (event) => {
            event.preventDefault();
            monitor("sdk | WebGLContextLost");
        };
        this.options = options;
        let canvasElement;
        if (isDefined(options) && isDefined(options.canvasElement)) {
            canvasElement = options.canvasElement;
        }
        let width = window.innerWidth;
        let height = window.innerHeight;
        if (isDefined(options) && isDefined(options["width"])) {
            width = options["width"];
        }
        if (isDefined(options) && isDefined(options["height"])) {
            height = options["height"];
        }
        this.renderer = new WebGLRenderer({
            canvas: canvasElement,
            antialias: true,
            preserveDrawingBuffer: true,
        });
        canvasElement.addEventListener("webglcontextlost", this._onWebGLContextLost);
        this.textureLoader = new TextureLoader();
        this.renderer.setSize(width, height);
        this.renderer.autoClear = false;
        this.renderer.shadowMap.enabled = true;
        // NOTE: containerElement: "infra3d div" and "canvasElement" = id of threejs webgl canvas
        if (isDefined(options) && isDefined(options["containerElement"]) && options["containerElement"] != "") {
            options.containerElement.appendChild(this.renderer.domElement);
        }
        else {
            document.body.appendChild(this.renderer.domElement);
        }
        this.imageCache = new Cache(256, false);
        this.textureCache = new Cache(256, false);
        this.dispMapCache = new Cache(256, false);
        this.symbolCache = new Cache(256, false);
        this.headerCache = new Cache(256, false);
        this._imageProvider = options.imageProvider;
    }
    getCanvas() {
        if (isDefined(this.renderer.domElement))
            return this.renderer.domElement;
        return null;
    }
    /**
     * Returns the Image Buffer of the specified rectangle. Coordinate
     * system starts in the upper left corner.
     * @method getImageBuffer
     * @param sx X Start Coordinate of the rectangle
     * @param sy Y Start Coordinate of the rectangle
     * @param width Width of the rectangle
     * @param height Height of the rectangle
     * @return Uint8Array Array containing RGBA values, so four array entries
     */
    getImageBuffer(sx, sy, width, height) {
        const gl = this.renderer.getContext();
        if (gl) {
            const arr = new Uint8Array(width * height * 4);
            gl.readPixels(sx, gl.drawingBufferHeight - (sy + height), width, height, gl.RGBA, gl.UNSIGNED_BYTE, arr);
            const image = new Uint8Array(width * height * 4);
            for (let y = 0; y < height; y++) {
                for (let x = 0; x < width; x++) {
                    const index = (y * width + x) * 4;
                    const index2 = ((height - 1 - y) * width + x) * 4;
                    for (let p = 0; p < 4; p++) {
                        image[index2 + p] = arr[index + p];
                    }
                }
            }
            return image;
        }
        return new Uint8Array(0);
    }
    requestTexture(imageUrl, uvMapping, imageLoadSuccess, imageLoadError, noCache) {
        return this.requestTextureFromImageUrl(imageUrl, uvMapping, imageLoadSuccess, imageLoadError, noCache);
    }
    /**
     * Acquire a texture from cache or load it
     * @method requestTexture
     * @param {string} imageUrl
     * @param {Mapping} uvMapping
     * @param {Function} imageLoadSuccess
     * @param {Function} imageLoadError
     * @param {boolean} noCache
     * @return Texture
     */
    requestTextureFromImageUrl(imageUrl, uvMapping, imageLoadSuccess, imageLoadError, noCache) {
        let texture = noCache ? null : this.textureCache.getItem(imageUrl);
        if (!isDefined(texture) || texture == null) {
            //store it to check if it's loading
            this.isLoading.push(imageUrl);
            const img = new Image();
            img.crossOrigin = this.crossorigin;
            texture = new Texture(img);
            texture.generateMipmaps = true;
            texture.name = imageUrl;
            img.onload = () => {
                if (!noCache)
                    this.textureCache.setItem(imageUrl, texture, {});
                this.isLoading.splice(this.isLoading.indexOf(imageUrl), 1);
                texture.needsUpdate = true;
                imageLoadSuccess(texture, imageUrl);
            };
            img.onerror = (error) => {
                this.isLoading.splice(this.isLoading.indexOf(imageUrl), 1);
                imageLoadError(error.toString());
                monitor("sdk | LoadTextureError", () => { }, { err: error.toString() });
            };
            img.src = this._imageProvider.getAuthImageURL(imageUrl);
        }
        else {
            imageLoadSuccess(texture, imageUrl);
            return texture;
        }
    }
    /**
     * Replaces {s} from url string with 'a','b' or 'c' for domain-sharding
     * @param url
     */
    getURLForArbitraryServer(url) {
        const options = ["a", "b", "c"];
        const server = options[Math.round(Math.random() * 2)];
        return url.replace("{s}", server);
    }
    getGenericUrl(url) {
        if (url.indexOf("//a.") > -1)
            return url.replace("//a.", "//{s}.");
        if (url.indexOf("//b.") > -1)
            return url.replace("//b.", "//{s}.");
        if (url.indexOf("//c.") > -1)
            return url.replace("//c.", "//{s}.");
        return url;
    }
    /**
     * Checks if a texture is already in cache! returns the texture or null! but doesn't start loading if its not in cache
     *
     * @param imageUrl
     * @returns {CACHEITEMSTATE}
     */
    isInTextureCache(imageUrl) {
        const texture = this.textureCache.getItem(imageUrl);
        if (isNull(texture)) {
            if (this.isLoading.indexOf(imageUrl) > -1) {
                return CACHEITEMSTATE.LOADING;
            }
            else {
                return CACHEITEMSTATE.NOTREQUESTEDFOR;
            }
        }
        if (isDefined(texture))
            return CACHEITEMSTATE.LOADED;
        return CACHEITEMSTATE.NOTREQUESTEDFOR;
    }
    /**
     * Acquire an image from cache or load it
     * @method requestImage
     * @param {string} imageUrl
     * @param {Function} imageLoadSuccess
     * @param {Function} imageLoadError
     * @param {boolean} noCache
     * @return Object
     */
    requestImage(imageUrl, imageLoadSuccess, imageLoadError, noCache) {
        const img = new Image();
        img.crossOrigin = this.crossorigin;
        img.onload = (event) => {
            imageLoadSuccess(img);
        };
        img.onerror = (event) => {
            imageLoadError();
            monitor("sdk | LoadImageError", undefined, { err: event.toString() });
        };
        img.src = this._imageProvider.getAuthImageURL(imageUrl);
    }
    requestDispMap(imageUrl, dispMapLoadSuccess, imageLoadError, noCache, tile) {
        return this.requestDispMapFromImageUrl(imageUrl, dispMapLoadSuccess, imageLoadError, noCache, tile);
    }
    /**
     * Acquire an image from cache or load it
     * @method requestDispMapFromImageUrl
     * @param {string} imageUrl
     * @param dispMapLoadSuccess
     * @param {Function} imageLoadError
     * @param {boolean} noCache
     * @param {DisparityTile} tile
     * @return Object
     */
    requestDispMapFromImageUrl(imageUrl, dispMapLoadSuccess, imageLoadError, noCache, tile) {
        let dispMap = noCache ? null : this.dispMapCache.getItem(imageUrl);
        if (!isDefined(dispMap) || dispMap == null) {
            //store it to check if it's loading
            this.isLoading.push(imageUrl);
            const img = new Image();
            img.crossOrigin = this.crossorigin;
            dispMap = new DisparityMap(tile.tileSize, tile.tileSize, img);
            dispMap.name = imageUrl;
            img.onload = (event) => {
                if (!noCache)
                    this.dispMapCache.setItem(imageUrl, dispMap, {});
                this.isLoading.splice(this.isLoading.indexOf(imageUrl), 1);
                dispMapLoadSuccess(dispMap);
            };
            img.onerror = (event) => {
                if (!noCache)
                    this.dispMapCache.setItem(imageUrl, null, {});
                this.isLoading.splice(this.isLoading.indexOf(imageUrl), 1);
                imageLoadError(dispMap);
                monitor("sdk | LoadDispMapError", undefined, { err: event.toString() });
            };
            img.src = this._imageProvider.getAuthDispmapURL(imageUrl);
        }
        else {
            dispMapLoadSuccess(dispMap);
        }
    }
    requestSymbol(symbolType, key, symbolLoadSuccess, symbolLoadError, fontmarkerdefinition) {
        let texture = this.symbolCache.getItem(key);
        if (!isDefined(texture) || texture == null) {
            const onload = (texture) => {
                this.symbolCache.setItem(key, texture, {});
                if (isDefined(symbolLoadSuccess) && symbolLoadSuccess != null)
                    symbolLoadSuccess(texture);
            };
            const onerror = (texture) => {
                this.symbolCache.setItem(key, null, {});
                if (isDefined(symbolLoadError) && symbolLoadError != null)
                    symbolLoadError(texture);
            };
            switch (symbolType) {
                case "fontMarker":
                    texture = StyleUtils.MakeTextTexture(fontmarkerdefinition, onload, onerror);
                    break;
                case "billboard":
                    texture = StyleUtils.MakeBillboardTexture(key, onload, onerror);
                    break;
            }
            this.symbolCache.setItem(key, texture, {});
        }
        return texture;
    }
    /**
     * Create ThreeJS Geometry Object from input data
     * @method createGeometry
     * @param {Vector3[]} vertices
     * @param {Face3[]} faces
     * @return {BufferGeometry}
     */
    createGeometry(vertices, index) {
        const geometry = new BufferGeometry();
        geometry.setFromPoints(vertices);
        geometry.setIndex(index);
        return geometry;
    }
    /**
     * Create ThreeJS mesh Object from input data
     * @method createGeometry
     * @param {BufferGeometry} geometry
     * @param {Material} material
     * @param {Object} position
     * @param {boolean} castShadow
     * @return {Mesh}
     */
    createMesh(geometry, material, position, castShadow) {
        const mesh = new Mesh(geometry, material);
        mesh.position.x = position.x;
        mesh.position.y = position.y;
        mesh.position.z = position.z;
        mesh.castShadow = castShadow;
        return mesh;
    }
    createMaterial(type, options) {
        if (type == "Lambert")
            return new MeshLambertMaterial(options);
        return null;
    }
    createBoxGeometry(width, height, depth, widthSegments, heightSegments, depthSegments) {
        let box;
        if (widthSegments) {
            if (heightSegments) {
                if (depthSegments) {
                    box = new BoxGeometry(width, height, depth, widthSegments, heightSegments, depthSegments);
                }
                else {
                    box = new BoxGeometry(width, height, depth, widthSegments, heightSegments);
                }
            }
            else {
                box = new BoxGeometry(width, height, depth, widthSegments);
            }
        }
        else {
            box = new BoxGeometry(width, height, depth);
        }
        return box;
    }
    /**
     * Returns width of the render context
     * @method getContextWidth
     * @return {number}
     */
    getContextWidth() {
        return this.renderer.getContext().drawingBufferWidth;
    }
    /**
     * Returns height of the render context
     * @method getContextHeight
     * @return {number}
     */
    getContextHeight() {
        return this.renderer.getContext().drawingBufferHeight;
    }
    /**
     * Set coordinate system centroid: Warning triggering this will update to all scenes and nodes
     * @method setCentroid
     * @param {number[]} coordinate
     */
    setCentroid(coordinate) {
        this.centroid = coordinate;
    }
    coordToInternT(easting, northing, height) {
        const a = easting - this.centroid[0];
        const b = northing - this.centroid[1];
        const c = height - this.centroid[2];
        return new Vector3(a, b, c);
    }
    internToCoordT(easting, northing, height) {
        const a = easting + this.centroid[0];
        const b = northing + this.centroid[1];
        const c = height + this.centroid[2];
        return new Vector3(a, b, c);
    }
    coordToIntern(easting, northing, height) {
        const a = easting - this.centroid[0];
        const b = northing - this.centroid[1];
        const c = height - this.centroid[2];
        return [a, b, c];
    }
    internToCoord(easting, northing, height) {
        const a = easting + this.centroid[0];
        const b = northing + this.centroid[1];
        const c = height + this.centroid[2];
        return [a, b, c];
    }
    /**
     * @description Resizes the canvas-element
     * @method resize
     * @param width
     * @param height
     */
    resize(width, height) {
        if (this.renderer)
            this.renderer.setSize(width, height, true);
    }
    destroy(destroyCaches = true) {
        this.options = null;
        this.textureLoader = null;
        if (destroyCaches) {
            this.imageCache.clear();
            this.imageCache = null;
            this.textureCache.clear();
            this.textureCache = null;
            this.dispMapCache.clear();
            this.dispMapCache = null;
            this.symbolCache.clear();
            this.symbolCache = null;
            this.headerCache.clear();
            this.headerCache = null;
        }
        this.renderer.domElement.removeEventListener("webglcontextlost", this._onWebGLContextLost);
        this.renderer.forceContextLoss();
        this.renderer.domElement = null;
        this.renderer = null;
    }
    /**
     * adds a slash to the end of the url if not present.
     * @param {string} url
     * @returns {string}
     */
    static correctUrl(url) {
        //Check if url ends with '/' else we add it.
        const suffix = "/";
        if (url.indexOf(suffix, url.length - suffix.length) == -1) {
            url += "/";
        }
        return url;
    }
}
//# sourceMappingURL=enginethree.js.map